/**
 * Galley shared domain + API contract.
 *
 * This file is the contract between packages/shared, apps/server and apps/web.
 * Add fields freely; never rename or remove one without updating every consumer.
 * Vocabulary (see docs/SPEC.md): Galley = one class's building, Room = one
 * assignment (one floor), Piece = one student's work, Walk = one gallery walk,
 * Stop = one passport stop, Note = one sticky note.
 */

import type { HouseholdDTO, SetupKind } from './museum/types'

// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------

export const GRADE_BANDS = ['4-5', '6-8', '9-12'] as const
export type GradeBand = (typeof GRADE_BANDS)[number]

export const THEME_IDS = ['galley', 'stacks', 'museum', 'conservatory'] as const
export type ThemeId = (typeof THEME_IDS)[number]

/** Viewer modes, chosen per viewer (never by the teacher for the class). */
export type ColorMode = 'day' | 'night' | 'projector' | 'contrast'

/** The school setup's five displays (and the ones a teacher's "Show as" offers). */
export const DISPLAY_TYPES = ['placard', 'book', 'frame', 'zine', 'booth'] as const
/**
 * Objects only the family setup uses (docs/family-setup.md §4.7): a pedestal for 3D works, a
 * VR headset for places (360° photos, 3D scenes), a lectern for one book shown open, and a
 * terminal for a website locked to its site. School pieces never get these.
 */
export const OBJECT_DISPLAY_TYPES = ['pedestal', 'headset', 'lectern', 'terminal'] as const
/** Every display a museum work may have: the school's five plus the family objects. */
export const MUSEUM_DISPLAY_TYPES = [...DISPLAY_TYPES, ...OBJECT_DISPLAY_TYPES] as const
export type SchoolDisplayType = (typeof DISPLAY_TYPES)[number]
export type DisplayType = SchoolDisplayType | (typeof OBJECT_DISPLAY_TYPES)[number]

/** Visual variants of a display type. Skins never change behaviour. */
export type Skin =
  | 'card' // placard: ≤25 words, response-wall card
  | 'placard' // placard: 26–250 words
  | 'broadside' // placard: poem
  | 'postcard' // placard: link / unknown / needs a look
  | 'novel' // book: long prose
  | 'chapbook' // book: long poem (>40 lines)
  | 'report' // book: lab report / research with headings
  | 'picture-book' // zine: doc with many images
  | 'print' // frame: normal image
  | 'banner' // frame: tall (h/w ≥ 2)
  | 'strip' // frame: wide (w/h ≥ 2.5)
  | 'zine' // zine: pages / slides / multi-image
  | 'kiosk' // booth: media ≤ 2 min
  | 'theater' // booth: media > 2 min
  | 'plinth' // pedestal: a 3D model, or an object photographed from several sides
  | 'headset' // headset: a 360° photo or a 3D place
  | 'lectern' // lectern: one book lying open
  | 'terminal' // terminal: a website (live, or its saved copy)

export type WalkPurpose = 'improve' | 'celebrate' | 'look'
export type NoteKind = 'glow' | 'grow' | 'wonder' | 'see' | 'think'
export type NoteStatus = 'pending' | 'approved' | 'rejected' | 'sent_back' | 'withdrawn'
export type NoteFlag =
  'profanity' | 'spanish_profanity' | 'teacher_word' | 'generic' | 'short' | 'safety'
export type WalkStatus = 'open' | 'paused' | 'ended' | 'returned'
export type PieceVisibility = 'named' | 'anonymous' | 'teacher_only'
export type PieceStatus = 'active' | 'hidden' | 'withdrawn'
export type RoomStatus = 'collecting' | 'hung' | 'archived'
export type NamesMode = 'first_last_initial' | 'none'
export type ReadingStyle = 'standard' | 'spacious' | 'wide'
/**
 * 'projector' = a teacher's class-visible view: no names by default, no private work, no notes.
 * 'visitor' = someone looking around a family museum (docs/museum-spec.md).
 */
export type ViewerKind = 'teacher' | 'student' | 'family' | 'projector' | 'visitor'
export type PieceSource = 'classroom-api' | 'demo' | 'upload' | 'addon'

/** What the importer found, before classification. */
export type ContentKind =
  | 'short_answer'
  | 'text' // Google Doc / plain text
  | 'image'
  | 'images' // several images
  | 'pdf'
  | 'slides'
  | 'audio'
  | 'video'
  | 'youtube'
  | 'link'
  | 'unknown'

// ---------------------------------------------------------------------------
// Rich text (student writing). Safe by construction: rendered as text nodes.
// ---------------------------------------------------------------------------

export type TextBlockKind = 'heading' | 'paragraph' | 'quote' | 'item'
export interface TextBlock {
  /** Stable id used for note anchors, e.g. "b3". */
  id: string
  kind: TextBlockKind
  /** Plain text. Line breaks (\n) are meaningful (poems). */
  text: string
}
export interface TextDoc {
  blocks: TextBlock[]
}

// ---------------------------------------------------------------------------
// Note anchors
// ---------------------------------------------------------------------------

export type NoteAnchor =
  | { type: 'block'; blockId: string }
  | { type: 'page'; index: number }
  | { type: 'time'; sec: number }
  | { type: 'point'; x: number; y: number } // 0..1 fractions of the image

export type Highlight =
  | { type: 'blocks'; blockIds: string[] }
  | { type: 'pages'; from: number; to: number }
  | { type: 'clip'; startSec: number; endSec: number }

// ---------------------------------------------------------------------------
// Classification
// ---------------------------------------------------------------------------

/** Features the importer extracts; input to classifyPiece(). */
export interface PieceFeatures {
  kind: ContentKind
  title?: string
  wordCount?: number
  lineCount?: number
  /** Share of lines with ≤ 10 words (0..1). */
  shortLineRatio?: number
  headingCount?: number
  hasReferences?: boolean
  imageCount?: number
  pageCount?: number
  wordsPerPage?: number
  durationSec?: number
  /** width / height of the main image or first page. */
  aspect?: number
  /** For 'link': hostname. */
  host?: string
}

export interface Classification {
  display: DisplayType
  skin: Skin
  /** 0..1. Below NEEDS_LOOK_CONFIDENCE the piece goes to the Needs a look tray. */
  confidence: number
  needsLook: boolean
  /** Human-readable reasons (English, for the teacher tray + tests). */
  reasons: string[]
  /**
   * The same reasons as stable codes for translation, one per reason: an id with values in
   * query-string form, e.g. "poem_lines?lines=14&shortPercent=80" (parseReasonCode()).
   */
  reasonCodes: string[]
  /** e.g. highlight required because reading time > 3 min. */
  highlightRequired: boolean
  highlightSuggested: boolean
}

// ---------------------------------------------------------------------------
// World layout (computed by layoutWorld() in packages/shared/src/world)
// ---------------------------------------------------------------------------

export type PropZone = 'floor' | 'ceiling' | 'wall-top' | 'end'
export interface PropPlacement {
  /** Prop id from the theme prop set, e.g. "fern", "stockpot". */
  prop: string
  zone: PropZone
  /** 0..1 along the floor/wall width. */
  x: number
  /** Scale multiplier 0.8..1.2 */
  scale: number
  flip: boolean
}

export interface StationLayout {
  pieceId: string
  /** Stable code "2B-04" (floor, bay letter, 2-digit spot). */
  code: string
  bay: string
  spot: number
  display: DisplayType
  skin: Skin
  /** Seeded cosmetic variation: rotation in degrees (−1.5..1.5), mounting prop. */
  tilt: number
  mount: 'pin' | 'tape' | 'clip' | 'easel' | 'hook' | 'stand'
}

export interface BayLayout {
  letter: string
  stations: StationLayout[]
  /**
   * A name for the bay, shown on the pillar before it (family setup: a hallway's year "2016",
   * a library's collection "Maya's poems"). Absent in the school setup.
   */
  title?: string
}

export interface FloorLayout {
  /** 0 = the ground-floor Galley lobby; rooms use their stable floor number. */
  level: number
  kind: 'lobby' | 'room'
  roomId?: string
  title: string
  /** Landmark prop id, e.g. "cake-stand"; kids say "the cake stand room". */
  landmark: string
  bays: BayLayout[]
  props: PropPlacement[]
}

export interface WorldLayout {
  version: 1
  theme: ThemeId
  /** Floors bottom-up: [lobby, ...visible rooms oldest→newest]. */
  floors: FloorLayout[]
  /** Rooms older than the newest MAX_VISIBLE_FLOORS, newest first. */
  archive: Array<{ roomId: string; level: number; title: string }>
}

/** Minimal input for layoutWorld(). */
export interface LayoutRoomInput {
  id: string
  floor: number
  title: string
  status: RoomStatus
  pieces: LayoutPieceInput[]
}
export interface LayoutPieceInput {
  id: string
  display: DisplayType
  skin: Skin
  /** Persisted placement; see placePieces()/tidyRoom(). */
  bay: string
  spot: number
}

// ---------------------------------------------------------------------------
// API DTOs
// ---------------------------------------------------------------------------

export interface ApiError {
  error: {
    /** Machine code, e.g. "not_found", "validation", "google_reconnect". */
    code: string
    /** Plain-language message safe to show to a teacher. */
    message: string
    details?: unknown
  }
}

export interface TeacherDTO {
  id: string
  name: string
  email: string | null
  isAdmin: boolean
  demo: boolean
  google: 'connected' | 'reconnect' | 'none'
}

export interface StudentDTO {
  memberId: string
  galleyId: string
  displayName: string
  comfort: ComfortSettings
  practiceDone: boolean
}

export interface ComfortSettings {
  calm: boolean | null // null = follow OS / class default
  night: boolean | null
  readingStyle: ReadingStyle
  contrast: boolean
}

export interface SessionDTO {
  teacher: TeacherDTO | null
  student: StudentDTO | null
  /** Family setup: the household profile signed in to the museum (docs/museum-spec.md). */
  household?: HouseholdDTO | null
  /** Send back as X-Galley-CSRF on every mutating request. */
  csrf: string
  instance: InstanceInfoDTO
}

export interface InstanceInfoDTO {
  name: string
  operator: string | null
  demoEnabled: boolean
  googleEnabled: boolean
  /** Only set when Google is enabled: needed by the client-side Picker. */
  google?: { clientId: string; apiKey: string | null; appId: string | null }
  aiEnabled: boolean
  version: string
  /** Days class data is kept after the class's last activity (RETENTION_DAYS). */
  retentionDays?: number
  /** The setup chosen at install (null until the setup chooser has run). */
  setup?: SetupKind | null
  /**
   * Setting up isn't finished: no setup chosen yet, or the family setup (chosen, or preset by
   * the installer) without its museum. `/` sends you to /setup.
   */
  needsSetup?: boolean
}

/** GET /api/t/picker-token: what the Google Picker needs in the teacher's browser. */
export interface PickerTokenDTO {
  /** Short-lived OAuth access token (drive.file); never stored by the client. */
  accessToken: string
  /** ISO time the token stops working; ask again before then. */
  expiresAt: string
  apiKey: string | null
  /** Cloud project number (Picker `setAppId`), so picked files are granted to Galley. */
  appId: string | null
}

export interface CourseDTO {
  id: string
  name: string
  section: string | null
  link: string | null
  updatedAt: string | null
  /** Galley already created for this course. */
  galleyId: string | null
  /** Guessed from name/section ("7th", "Grade 10"). */
  suggestedBand: GradeBand | null
}

export interface GalleySummaryDTO {
  id: string
  name: string
  section: string | null
  theme: ThemeId
  gradeBand: GradeBand
  demo: boolean
  roomCount: number
  studentCount: number
  activeWalkId: string | null
  pendingNotes: number
  updatedAt: string
  /** Set when class data will be deleted for inactivity within 30 days (retention). */
  deletionWarningAt?: string | null
  /**
   * The most recent walk (running, ended or returned; not deleted) that still has notes waiting
   * for review; `pendingNotes` counts that walk's pending notes only.
   */
  pendingWalkId?: string | null
  /**
   * When the teacher deleted this Galley (listed by GET /api/t/galleys?deleted=1; it can be
   * restored for 30 days). Null for Galleys in use.
   */
  deletedAt?: string | null
}

export interface GalleySettingsDTO {
  namesMode: NamesMode
  calmDefault: boolean
  familyEnabled: boolean
  familyShowClassmates: boolean
  /** Extra words the teacher wants flagged in notes. */
  teacherWords: string[]
  practiceEnabled: boolean
}

export interface MemberDTO {
  id: string
  givenName: string
  familyName: string
  displayName: string
  neverShowName: boolean
  neverFamily: boolean
  dontExhibit: boolean
  excuseVisiting: boolean
  glowWonderOnly: boolean
  signedIn: boolean
  /**
   * Set while the picture card is resting after too many wrong tries (5, then 15, then 60
   * minutes); POST /api/t/members/:id/card-unlock ends it. QR and Google sign-in still work.
   */
  cardLockedUntil?: string | null
}

export interface GalleyDetailDTO extends GalleySummaryDTO {
  /**
   * Set when picture sign-in stopped for the whole class after too many wrong tries in a day
   * (SPEC §3.7). QR codes and Google sign-in still work; POST /api/t/galleys/:id/cards/unlock
   * (or unlocking any student's card) starts it again.
   */
  cardsStoppedAt?: string | null
  doorCode: string
  courseLink: string | null
  settings: GalleySettingsDTO
  rooms: RoomSummaryDTO[]
  walks: WalkSummaryDTO[]
  members: MemberDTO[]
}

export interface CourseworkDTO {
  id: string
  title: string
  workType: 'ASSIGNMENT' | 'SHORT_ANSWER_QUESTION' | 'MULTIPLE_CHOICE_QUESTION'
  link: string | null
  updatedAt: string | null
  turnedInCount: number
  totalCount: number
  hasDriveFiles: boolean
  /** Room already hung for this coursework. */
  roomId: string | null
  /** Multiple-choice work cannot be exhibited. */
  exhibitable: boolean
}

export interface RoomSummaryDTO {
  id: string
  floor: number
  title: string
  status: RoomStatus
  pieceCount: number
  needsLookCount: number
  pendingFileCount: number
  courseworkId: string | null
  courseworkLink: string | null
  displayOverride: DisplayType | null
  lastSyncedAt: string | null
  createdAt: string
}

export interface RoomDetailDTO extends RoomSummaryDTO {
  signText: string
  studentCheck: boolean
  pieces: PieceTeacherDTO[]
  /** Drive files still waiting for the teacher to pick them in the Picker. */
  pendingFiles: Array<{ fileId: string; title: string | null; memberId: string | null }>
  /** Google Drive folder with the students' files (Picker fallback). */
  studentWorkFolderId: string | null
}

/** Author as shown to viewers (already privacy-filtered by the server). */
export interface AuthorLabel {
  /** "Maya R." or null when hidden. */
  name: string | null
  /** True when this viewer is (one of) the authors. */
  isYou: boolean
}

export interface PieceSummaryDTO {
  id: string
  roomId: string
  code: string
  display: DisplayType
  skin: Skin
  title: string
  authors: AuthorLabel[]
  /** "Essay · 1,240 words · ~7 min" */
  mediumLine: string
  /** ≤ 50 words shown at Glance/Stop level. */
  excerpt: string | null
  blurb: string | null
  noticeThis: string | null
  /** Signed URLs. */
  thumbUrl: string | null
  thumbAspect: number | null
  lang: string | null
  /** New since this viewer's last visit (never on projector). */
  fresh: boolean
  /** Tiny (≈96 px) rendition for the building's miniatures; falls back to thumbUrl. */
  miniUrl?: string | null
  /** Inputs for formatting the medium line in the viewer's language (mediumLine() in shared). */
  mediumFeatures?: PieceFeatures | null
  /**
   * Family setup, a website on a terminal: its address's host for the label under the screen
   * ("maya.example"); null for a saved copy with no live site. Absent for everything else.
   */
  host?: string | null
}

/** A student's own piece (GET /api/s/me `myPieces`, PATCH /api/s/pieces/:id). */
export interface StudentPieceDTO extends PieceSummaryDTO {
  /** Who sees it now: the student's choice, made more private by a teacher override. */
  visibility: PieceVisibility
  /** The student's own choice (differs from `visibility` when the teacher overrode it). */
  chosenVisibility: PieceVisibility
  /** The room asks students to check their work (title, highlight) before the walk. */
  studentCheck: boolean
}

export interface PieceDetailDTO extends PieceSummaryDTO {
  roomTitle: string
  text: TextDoc | null
  pages: Array<{ url: string; width: number; height: number; text: string | null }>
  imageUrl: string | null
  imageAlt: string | null
  media: {
    kind: 'audio' | 'video' | 'youtube'
    url: string | null
    youtubeId: string | null
    posterUrl: string | null
    durationSec: number | null
    transcript: TextDoc | null
    captionsUrl: string | null
  } | null
  link: { url: string; host: string; title: string | null; allowed: boolean } | null
  highlight: Highlight | null
  visitorQuestion: string | null
  wordCount: number | null
  /** Notes for this piece visible to this viewer (author after Return; teacher always). */
  notes: NoteDTO[]
}

export interface PieceTeacherDTO extends PieceSummaryDTO {
  memberIds: string[]
  status: PieceStatus
  visibility: PieceVisibility
  needsLook: boolean
  confidence: number
  /** Why the piece is shown as it is, in English (kept for older clients). */
  reasons: string[]
  /**
   * The same reasons as stable codes the web translates, one per reason, in the same order:
   * an id with values in query-string form ("poem_lines?lines=14&shortPercent=80",
   * "import_problem?problem=too_big&mb=25&title=Final.mp4"); read them with parseReasonCode().
   * The ids are listed in docs/SPEC.md §3.2.
   */
  reasonCodes?: string[]
  changedSinceSnapshot: boolean
  source: PieceSource
}

export interface WorldDTO {
  galley: {
    id: string
    name: string
    section: string | null
    theme: ThemeId
    gradeBand: GradeBand
    demo: boolean
    calmDefault: boolean
  }
  layout: WorldLayout
  rooms: Array<{ id: string; floor: number; title: string; signText: string; status: RoomStatus }>
  pieces: PieceSummaryDTO[]
  viewer: {
    kind: ViewerKind
    memberId: string | null
    /** Active walk for this viewer, with their passport (students). */
    walk: StudentWalkDTO | null
    /** Pieces locked until the passport is done (student during a walk). */
    lockedPieceIds: string[]
  }
}

export interface WalkSummaryDTO {
  id: string
  purpose: WalkPurpose
  status: WalkStatus
  roomIds: string[]
  stopsPerStudent: number
  notesLocked: boolean
  createdAt: string
  endedAt: string | null
  returnedAt: string | null
  pendingNotes: number
  approvedNotes: number
  /** Approved notes not yet returned (after a first Return); 0 before any Return. */
  approvedSinceReturn?: number
}

export interface WalkSettingsInput {
  purpose: WalkPurpose
  roomIds: string[]
  stopsPerStudent: number
  signedNotes: boolean
  lookFor: string | null
  exitTicket: boolean
}

export interface StopDTO {
  id: string
  pieceId: string
  /** Room the stop's piece hangs in, so a stop can link to its wall before the world loads. */
  roomId?: string
  code: string
  order: number
  bonus: boolean
  opened: boolean
  done: boolean
  /** Kinds already written at this stop. */
  written: NoteKind[]
  /** Notes the teacher sent back with a nudge; the student may rewrite that kind. */
  sentBack?: Array<{ noteId: string; kind: NoteKind; body: string; nudge: string | null }>
}

export interface StudentWalkDTO {
  id: string
  purpose: WalkPurpose
  status: WalkStatus
  notesLocked: boolean
  signedNotes: boolean
  lookFor: string | null
  stops: StopDTO[]
  passportDone: boolean
  /** Practice required before the first stop. */
  practiceRequired: boolean
  /** Notes were returned and this student has pieces with notes. */
  returnReady: boolean
  reflected: boolean
  exitTicket: boolean
}

export interface NoteDTO {
  id: string
  pieceId: string
  kind: NoteKind
  starter: string | null
  body: string
  anchor: NoteAnchor | null
  status: NoteStatus
  endorsed: boolean
  /** Only teachers (or signed walks) see authors. */
  authorName: string | null
  authorMemberId: string | null
  flags: NoteFlag[]
  flagTerms: string[]
  nudge: string | null
  createdAt: string
  /** Epoch ms until which the author may Unsend. */
  unsendUntil: number | null
}

export interface NoteInput {
  walkId: string
  pieceId: string
  kind: NoteKind
  starter: string | null
  body: string
  anchor: NoteAnchor | null
}

export interface NoteValidation {
  ok: boolean
  /** Hard errors: note not accepted. */
  errors: Array<
    'too_short' | 'too_long' | 'link' | 'email' | 'phone' | 'handle' | 'empty' | 'blanks'
  >
  /** Soft nudges: accepted only when the student confirms (e.g. generic). */
  nudges: Array<'generic' | 'which_part'>
  /** Words the student wrote themselves (the starter's words don't count). */
  wordCount: number
  /**
   * Every word in the note, a sentence starter's included. A note passes the length rule when
   * this reaches the band minimum and `wordCount` reaches NOTE_MIN_OWN_WORDS (SPEC §3.5).
   */
  totalWords: number
}

export type ModerationAction =
  'approve' | 'reject' | 'send_back' | 'endorse' | 'unendorse' | 'restore' // undo → pending

export interface ReviewQueueDTO {
  walk: WalkSummaryDTO
  /** Grouped by piece; flagged notes first inside and across groups. */
  groups: Array<{
    piece: { id: string; code: string; title: string; authorName: string | null }
    notes: NoteDTO[]
  }>
  counts: { pending: number; flagged: number; approved: number; rejected: number; sentBack: number }
}

export interface WalkConsoleDTO {
  walk: WalkSummaryDTO & WalkSettingsInput
  students: Array<{
    memberId: string
    displayName: string
    stopsDone: number
    stopsTotal: number
    notesWritten: number
    practiceDone: boolean
    reflected: boolean
    signedIn: boolean
  }>
  /**
   * Pieces with fewer distinct visitors (writers of pending or approved notes) than their
   * passport planned, and every piece still without a visitor (teacher only). `notes` counts
   * those notes.
   */
  needsVisitors: Array<{ pieceId: string; code: string; title: string; notes: number }>
  estimateMinutes: { walking: number; reading: number }
  /**
   * Teacher only, never on the projector or for students: exit answers and notes that trip
   * the safety filter (self-harm, violence, abuse), oldest first. Notes are listed whatever
   * their status, withdrawn ones included.
   */
  needsAttention?: Array<{
    memberId: string
    displayName: string
    text: string
    source: 'exit' | 'note'
    createdAt: string
  }>
  /**
   * Teacher only: exit answers the other class filters flag (profanity, the teacher's words).
   * They stay off the projector debrief; safety-flagged answers are in `needsAttention`.
   */
  exitAnswersFlagged?: Array<{
    memberId: string
    displayName: string
    text: string
    flags: string[]
  }>
}

export interface ReturnDTO {
  walkId: string
  purpose: WalkPurpose
  pieces: Array<{
    piece: PieceSummaryDTO
    notes: NoteDTO[]
  }>
  reflection: ReflectionDTO | null
  exitPrompt: string | null
}

export interface ReflectionInput {
  pieceId: string | null
  usedNoteIds: string[]
  plan: string
  thanked: boolean
  exitAnswer: string | null
}
export interface ReflectionDTO extends ReflectionInput {
  createdAt: string
}

export interface DebriefDTO {
  walk: WalkSummaryDTO
  endorsed: Array<{ note: NoteDTO; piece: PieceSummaryDTO }>
  picks: PieceSummaryDTO[]
  /** Wonder notes (approved), for the teacher to group. */
  wonders: NoteDTO[]
  /** Exit-ticket answers for the projector; answers the note filters flag are left out. */
  exitAnswers: string[]
  /** How many exit-ticket answers were left out because a filter flagged them. */
  exitAnswersHidden?: number
}

export interface JoinLookupDTO {
  galleyId: string
  galleyName: string
  theme: ThemeId
  members: Array<{ id: string; displayName: string }>
  /** Picture ids offered on the picker grid (the full set, same for all). */
  pictures: string[]
  googleEnabled: boolean
}

export interface SignInCardDTO {
  memberId: string
  displayName: string
  pictures: string[]
  /** Full URL encoded in the QR code. */
  qrUrl: string
}

export interface FamilyViewDTO {
  galley: { name: string; theme: ThemeId }
  student: { displayName: string } | null
  pieces: PieceDetailDTO[]
  classmates: PieceSummaryDTO[]
  expiresAt: string
}

export interface FamilyLinkDTO {
  id: string
  memberId: string
  displayName: string
  url: string
  expiresAt: string
  revoked: boolean
}

export interface TechKitDTO {
  instanceName: string
  operator: string | null
  baseUrl: string
  oauthClientId: string | null
  scopes: Array<{ scope: string; why: string }>
  extensionId: string | null
  extensionPolicyJson: string
  privacyUrl: string
  accessibilityUrl: string
}

export interface ResolveLinkDTO {
  courseId: string | null
  courseworkId: string | null
  galleyId: string | null
  roomId: string | null
}

export type ServerEvent =
  | { type: 'walk'; walkId: string; galleyId: string }
  | { type: 'notes'; walkId: string; galleyId: string }
  | { type: 'returned'; walkId: string; galleyId: string }
  | { type: 'room'; roomId: string; galleyId: string }
  | { type: 'progress'; walkId: string; galleyId: string }
  | { type: 'galley'; galleyId: string }
